import { ACTIVE_TAB_PARAM } from '@/domain/common/hooks/useActiveTabParam';

import { IAjudaTela } from './ajudaConteudo';

/**
 * Qual tela do manual responde pelo endereço aberto.
 *
 * O "?" do cabeçalho vale para QUALQUER tela, então ele não pode depender de cada página
 * dizer quem ela é: a rota já diz. A ligação sai do próprio manual (`ajuda.telas.item.*.
 * rota`), que é o mesmo endereço que o cartão da tela abre, e assim não existe uma
 * segunda lista de rotas para alguém esquecer de atualizar.
 */

interface IPartesDaRota {
  caminho: string;
  /** A aba que a rota pede (`/tempos?activeTab=sequencias`), ou `null` quando é rota simples. */
  aba: string | null;
}

/** `/fila/` e `/fila` são o mesmo lugar; a raiz continua sendo `/`. */
function semBarraFinal(caminho: string): string {
  const limpo = caminho.trim();
  return limpo.length > 1 && limpo.endsWith('/') ? limpo.slice(0, -1) : limpo;
}

function partesDaRota(rota: string): IPartesDaRota {
  const [caminho = '', busca = ''] = rota.split('?');
  return { caminho: semBarraFinal(caminho), aba: new URLSearchParams(busca).get(ACTIVE_TAB_PARAM) };
}

/**
 * A tela do manual do endereço atual, ou `null` quando aquele endereço não é uma tela do
 * manual (a própria Ajuda, uma rota de detalhe, uma rota antiga que só redireciona).
 *
 * Uma ÁREA com abas tem uma tela do manual por aba, e por isso a aba entra na conta: em
 * Tempos, o "?" da Curadoria tem de abrir a Curadoria, não as Sequências. Endereço
 * sem aba nenhuma abre a aba padrão da área, que é a primeira que o manual lista.
 */
export function telaDaRota(
  telas: Array<IAjudaTela>,
  pathname: string,
  abaAberta: string | null,
): IAjudaTela | null {
  const caminho = semBarraFinal(pathname);
  const candidatas = telas.filter((tela) => partesDaRota(tela.rota).caminho === caminho);
  if (candidatas.length === 0) {
    return null;
  }
  const daAba = candidatas.find((tela) => partesDaRota(tela.rota).aba === abaAberta);
  const semAba = candidatas.find((tela) => partesDaRota(tela.rota).aba === null);
  return daAba ?? semAba ?? candidatas[0] ?? null;
}
